import { apiClient } from '@/lib/api/client';
import type { Company } from '../types/company.types';

export const companyService = {
  /**
   * Get the selected company with all details including certificates
   */
  async getSelectedCompany(): Promise<Company | null> {
    // Get the selected company with all details
    const response = await apiClient.get('/companies/selected');
    const selectedCompany = response.data.company;

    return selectedCompany?.id ? selectedCompany : null;
  },

  /**
   * Get company settings by ID
   * Uses the selected company ID from the session
   * This method fetches full company details from Aludra API for editing
   */
  async getCompanySettings(): Promise<Company> {
    // First get the selected company to get its ID
    const selectedResponse = await apiClient.get('/companies/selected');
    const selectedCompany = selectedResponse.data.company;

    if (!selectedCompany?.id) {
      throw new Error('No company selected');
    }

    // Then fetch the full company details from Aludra
    const response = await apiClient.get(`/companies/${selectedCompany.id}`);
    return response.data;
  },

  /**
   * Update company settings
   */
  async updateCompanySettings(settings: Partial<Company>): Promise<Company> {
    // First get the selected company to get its ID
    const selectedResponse = await apiClient.get('/companies/selected');
    const selectedCompany = selectedResponse.data.company;

    if (!selectedCompany?.id) {
      throw new Error('No company selected');
    }

    // Update the company
    const response = await apiClient.put(`/companies/${selectedCompany.id}`, settings);
    return response.data;
  },

  /**
   * Get country information by countryId
   */
  async getCountryById(countryId: string): Promise<any> {
    const response = await apiClient.get(`/companies/country/${countryId}`);
    return response.data;
  },

  /**
   * Upload a file to Aludra cloud storage via BFF
   * @param file - The file to upload
   * @param companyId - The company ID
   * @param entityType - The entity type (default: 'customer')
   * @returns The uploaded file URL
   */
  async uploadFile(
    file: File,
    companyId: string,
    entityType: string = 'customer'
  ): Promise<{ url: string }> {
    const tenantId = import.meta.env.VITE_DEFAULT_TENANT || '172';

    const formData = new FormData();
    formData.append('file', file);
    formData.append('companyId', companyId);
    formData.append('entityType', entityType);
    formData.append('tenantId', tenantId);

    console.log('📤 Uploading file via BFF:', {
      originalName: file.name,
      companyId,
      entityType,
      tenantId,
      size: file.size,
    });

    const response = await apiClient.post(
      '/companies/upload-file',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    console.log('✅ File uploaded successfully:', response.data);

    return response.data;
  },
};
